/**
 * Oct 20 founder addendum (L3) — schedule the remaining work of a repair whose vehicle came back, as a NEW linked
 * continuation of the same repair (create_return_continuation). The old released days are never re-booked: the person
 * chooses when the remaining work starts, how long it should take and, optionally, the technician. The continuation
 * stays with the front office until it is handed to the shop (FOH → BOH) through the custody authority.
 */
import { useEffect, useState } from 'react';
import { fromZonedTime } from 'date-fns-tz';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { createReturnContinuation, type ActAnswer } from '@/lib/foh/pickup/departureActs';
import { shopToday } from '@/lib/foh/workboard/workboardActions';

export interface ReturnContinuationDialogProps {
  open: boolean;
  ticketId: string | null;
  shopId: string;
  shopTimezone: string;
  subject: string | null;
  onClose: () => void;
  onDone: (answer: ActAnswer) => void;
}

/** "YYYY-MM-DD" + "HH:MM" in the shop's zone → an ISO instant; null when incomplete or invalid. */
export function continuationStartIso(date: string, time: string, timeZone: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const d = fromZonedTime(`${date}T${time}:00`, timeZone);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

export function ReturnContinuationDialog({ open, ticketId, shopId, shopTimezone, subject, onClose, onDone }: ReturnContinuationDialogProps) {
  const today = shopToday(shopTimezone);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState('08:00');
  const [hours, setHours] = useState('2');
  const [tech, setTech] = useState('');
  const [note, setNote] = useState('');
  const [techs, setTechs] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(shopToday(shopTimezone)); setTime('08:00'); setHours('2'); setTech(''); setNote(''); setRefusal(null);
    let live = true;
    void (supabase as any).from('technicians').select('id, name, employment_status').eq('shop_id', shopId)
      .then(({ data }: { data: Array<{ id: string; name: string; employment_status: string | null }> | null }) => {
        if (live) setTechs((data ?? []).filter((t) => t.employment_status !== 'Inactive').map((t) => ({ id: t.id, name: t.name })));
      });
    return () => { live = false; };
  }, [open, shopId, shopTimezone]);

  const minutes = Math.round(Number(hours) * 60);
  const start = continuationStartIso(date, time, shopTimezone);
  const problem = !start ? 'Choose a start date and time.'
    : date < today ? 'The start cannot be in the past.'
    : !(minutes > 0 && minutes <= 24 * 60) ? 'Enter the expected hours (more than 0).'
    : null;

  const submit = async () => {
    if (!ticketId || !start || problem || busy) return;
    setBusy(true);
    try {
      const a = await createReturnContinuation({ ticketId, startTime: start, predictedMinutes: minutes,
        technicianId: tech || null, note: note.trim() || null });
      if (!a.ok) { setRefusal(a.message); return; }
      onDone(a);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="return-continuation-dialog">
        <DialogHeader>
          <DialogTitle>Schedule the remaining work</DialogTitle>
          <DialogDescription>
            {subject ? `${subject}: ` : ''}a new day continues this repair (same job lines, notes and history). The days
            released when the customer left are not re-booked.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="rc-date">Start date</Label>
            <Input id="rc-date" type="date" min={today} value={date} disabled={busy} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rc-time">Start time</Label>
            <Input id="rc-time" type="time" value={time} disabled={busy} onChange={(e) => setTime(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rc-hours">Expected hours</Label>
            <Input id="rc-hours" type="number" min={0.25} step={0.25} value={hours} disabled={busy} onChange={(e) => setHours(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="rc-tech">Technician (optional)</Label>
            <select id="rc-tech" className="h-10 w-full rounded-md border bg-background px-2 text-sm" value={tech} disabled={busy}
                    onChange={(e) => setTech(e.target.value)} data-testid="return-continuation-tech">
              <option value="">Unassigned</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <Label htmlFor="rc-note" className="text-sm">Note (optional)</Label>
        <Textarea id="rc-note" rows={2} maxLength={500} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)} />
        {(refusal || problem) && (
          <p className={refusal ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'} data-testid="return-continuation-message">
            {refusal ?? problem}
          </p>
        )}
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="min-h-11" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="min-h-11" disabled={!!problem || busy} onClick={() => { void submit(); }} data-testid="return-continuation-confirm">
            {busy ? 'Scheduling…' : 'Schedule continuation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
